import { useCallback, useMemo, useState } from "react";
import { contactsService } from "@/services/contactsService";
import { usePagedResource } from "@/hooks/usePagedResource";
import { usePageScope, useSettingsStore } from "@/store/settingsStore";
import { useCrmMappingSyncStore } from "@/store/crmMappingSyncStore";
import { contactPropertyListScope } from "@/lib/contactPropertyListScope";
import { scopeParams } from "@/lib/scopeParams";
import type { ContactFilter, ContactSummary } from "@/types/entities";
import type { PageRequest, PageResponse } from "@/types/api";

export { labelIdsFromSearchParams } from "./contactFilterParams";

export function useContactsList(initialFilter?: ContactFilter, initialPage?: PageRequest) {
  const { legalEntityId, branchId } = useSettingsStore();
  const scope = usePageScope("contacts");
  const crmMappingEpoch = useCrmMappingSyncStore((s) => s.crmMappingEpoch);
  const scopeQuery = useMemo(() => scopeParams(scope), [scope]);

  const fetcher = useCallback(
    (filter: ContactFilter, page: PageRequest): Promise<PageResponse<ContactSummary>> =>
      contactsService.list(
        {
          ...filter,
          ...contactPropertyListScope(legalEntityId, branchId),
          ...scopeQuery,
        },
        page,
      ),
    [legalEntityId, branchId, scopeQuery, crmMappingEpoch],
  );

  const resource = usePagedResource<ContactSummary, ContactFilter>(fetcher, {
    initialPage: initialPage ?? { page: 0, size: 30, sort: "atlasModifiedAt", direction: "desc" },
    initialFilter,
  });

  const { filter, pageRequest } = resource;

  /**
   * Walks every page behind the current filter/sort and returns the rows. Backs
   * "select all across pages" - there is no bulk-by-filter endpoint, so the ids
   * have to be collected client-side before an action can run over them.
   */
  const fetchAllPages = useCallback(async (): Promise<ContactSummary[]> => {
    const rows: ContactSummary[] = [];
    for (let page = 0; page < MAX_SELECT_ALL_PAGES; page += 1) {
      const res = await fetcher(filter, { ...pageRequest, page, size: SELECT_ALL_PAGE_SIZE });
      rows.push(...(res.content ?? []));
      if ((res.content?.length ?? 0) === 0 || page + 1 >= (res.totalPages ?? 0)) break;
    }
    return rows;
  }, [fetcher, filter, pageRequest]);

  return { ...resource, fetchAllPages };
}

/** Page size and page cap used when collecting every contact behind the filter. */
const SELECT_ALL_PAGE_SIZE = 200;
const MAX_SELECT_ALL_PAGES = 50;

/**
 * Multi-select state for the contacts table.
 *
 * Selection is keyed by id and survives paging, sorting and filtering � an operator
 * culling records across several pages would otherwise lose everything they picked
 * the moment they clicked "next". Names are remembered alongside the ids so the
 * confirmation dialog can still label a contact selected three pages ago.
 */
export function useContactSelection() {
  const [selected, setSelected] = useState<Record<string, string>>({});

  const selectedIds = useMemo(() => Object.keys(selected), [selected]);
  const selectedKeys = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = useCallback((row: ContactSummary, next: boolean) => {
    setSelected((current) => {
      const updated = { ...current };
      if (next) updated[row.id] = row.name || row.contactPerson || row.id;
      else delete updated[row.id];
      return updated;
    });
  }, []);

  const toggleAll = useCallback((rows: ContactSummary[], next: boolean) => {
    setSelected((current) => {
      const updated = { ...current };
      for (const row of rows) {
        if (next) updated[row.id] = row.name || row.contactPerson || row.id;
        else delete updated[row.id];
      }
      return updated;
    });
  }, []);

  const clear = useCallback(() => setSelected({}), []);

  /** Drops specific ids � used after a delete so the bar empties without a refetch. */
  const remove = useCallback((ids: string[]) => {
    setSelected((current) => {
      const updated = { ...current };
      for (const id of ids) delete updated[id];
      return updated;
    });
  }, []);

  return { selectedIds, selectedKeys, labels: selected, toggle, toggleAll, clear, remove };
}

